import React, { useState, useMemo } from "react";
import {
  Search, ChevronDown, ChevronRight, ShieldCheck, BookOpen, Workflow,
  RefreshCw, PackageCheck, Receipt, ScanLine, Package, Truck, ShoppingCart, Tag,
  CreditCard, Printer, PackageMinus, BookMarked, Wallet, ArrowRightLeft, CalendarClock,
  ClipboardList, BarChart3, Factory, Layers, Scissors, Shirt, ClipboardCheck,
  FileText, ShoppingBag, PackagePlus, Boxes, FilePlus, CheckCircle, Undo2, TrendingDown,
} from "lucide-react";
import { PageHeader, Panel } from "../lib/ui";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Download } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/roles";

// ================= Panduan Role =================
const ROLES = [
  { key: "owner", desc: "Akses penuh ke SEMUA menu & aksi, termasuk hapus data, konfigurasi sistem, Konektor ChatGPT, dan Konfigurasi PJ RAB. Tidak dibatasi izin apa pun." },
  { key: "warehouse_admin", desc: "Kelola stok, transfer antar gudang, gudang & rak, penerimaan barang, dan daftar stok produk." },
  { key: "purchasing", desc: "Buat & kelola Purchase Request dan Purchase Order, data supplier, serta penerimaan barang." },
  { key: "qc_team", desc: "Pemeriksaan kualitas barang masuk: menentukan lolos, perlu rework, atau reject." },
  { key: "supervisor", desc: "Pengawasan operasional gudang, menyetujui transfer/opname, dan melihat laporan gudang." },
  { key: "sales", desc: "Transaksi kasir, Keep, Pre-Order, data pelanggan, dan penjualan online (Ventedaily)." },
  { key: "picker", desc: "Mengambil barang pesanan (picking) untuk disiapkan dikirim." },
  { key: "packer", desc: "Packing pesanan dan penyerahan paket ke kurir." },
  { key: "rnd", desc: "Riset & pengembangan produk, menyusun BOM/resep, dan merencanakan produk baru." },
  { key: "production_admin", desc: "Input & kelola data produksi: cutting, material, dan order ke konveksi." },
  { key: "production_spv", desc: "Pengawasan produksi, menyetujui rencana produksi dan penerimaan hasil produksi." },
  { key: "cutting", desc: "Menjalankan proses pemotongan kain (cutting order)." },
  { key: "accounting", desc: "Keuangan: biaya, settlement marketplace, rekonsiliasi bank, dan review RAB." },
  { key: "viewer", desc: "Melihat laporan & data keuangan (umumnya read-only / terbatas)." },
];

const ROLE_MODEL = [
  "Owner selalu memiliki akses penuh dan tidak bisa dibatasi.",
  "Role bawaan: otomatis bisa lihat & edit semua menu; hapus (delete) bersifat opsional per role.",
  "Custom Role: izin diatur granular per menu & per aksi (lihat, detail, buat/ubah, hapus, setujui, bayar, dll) di menu User and Role → Custom Role.",
  "Role Tambahan (sementara): seorang user bisa diberi peran tambahan dengan masa berlaku, mis. akses Owner sementara.",
  "Menu yang tidak diizinkan untuk sebuah role otomatis disembunyikan dari sidebar user tersebut.",
];

// roles: daftar role (selain owner) yang relevan. "all" = untuk semua role. [] = khusus Owner.
const SECTIONS = [
  { section: "Dashboard", menus: [
    { name: "Dashboard", roles: ["all"], desc: "Ringkasan KPI bisnis: total penjualan, jumlah transaksi, HPP, profit, diskon, nilai Keep & Pre-Order; tren penjualan & item terjual; aktivitas user; top produk & pelanggan; serta kartu 'Paket Belum Dikirim'." },
  ]},
  { section: "Sales", menus: [
    { name: "Cashier (Kasir)", roles: ["sales"], desc: "Membuat transaksi penjualan (POS): pilih produk/varian, beri diskon, pilih metode bayar, dan cetak struk." },
    { name: "Sale List", roles: ["sales", "accounting"], desc: "Daftar seluruh penjualan. Lihat detail, status pembayaran, status retur, dan cetak ulang struk." },
    { name: "Shipping Sale", roles: ["sales", "packer"], desc: "Mengelola pengiriman untuk penjualan yang perlu dikirim ke pelanggan." },
    { name: "Keep (Create/List)", roles: ["sales"], desc: "Keep = titip/booking barang untuk reseller/pelanggan dengan DP. Buat keep, pantau jatuh tempo, konversi ke penjualan, atau batalkan." },
    { name: "Pre-Order — Overview PO", roles: ["sales"], desc: "Ringkasan seluruh aktivitas pre-order dalam satu layar." },
    { name: "Pre-Order — Create Keep PO", roles: ["sales"], desc: "Membuat pesanan pre-order (keep PO) dari pelanggan untuk batch yang sedang dibuka." },
    { name: "Pre-Order — Campaign & Batch", roles: ["sales"], desc: "Mengelola kampanye & batch PO: periode, produk, dan kuota tiap batch." },
    { name: "Pre-Order — Pesanan PO", roles: ["sales", "warehouse_admin"], desc: "Daftar pesanan PO dan pemenuhannya per gudang (alokasi stok siap kirim)." },
    { name: "Pre-Order — Analisis Produk & Varian", roles: ["sales"], desc: "Analisis permintaan PO per produk, warna, dan ukuran." },
    { name: "Pre-Order — Kebutuhan Produksi", roles: ["sales", "production_admin", "production_spv"], desc: "Menghitung kebutuhan produksi berdasarkan pesanan PO vs stok tersedia." },
    { name: "Pre-Order — Kuota Pre-Order", roles: ["sales"], desc: "Mengatur kuota planned/reserved/sold/remaining per varian & batch." },
    { name: "Ventedaily — Online Sales", roles: ["sales", "accounting"], desc: "Rekap penjualan online (marketplace) yang masuk melalui Desty." },
    { name: "Ventedaily — Desty Integrate", roles: ["sales"], desc: "Koneksi & sinkronisasi order Desty (TikTok/Shopee/dll). Termasuk auto-keep, auto-kasir, dan routing stok (mis. Gudang Bandung)." },
    { name: "Ventedaily — Paket Belum Dikirim", roles: ["sales", "packer", "picker"], desc: "Pengingat order marketplace yang BELUM diserahkan ke kurir, diurutkan dari deadline: terlambat, segera (<3 jam), wajib hari ini, dijadwalkan." },
    { name: "Ventedaily — Order Cancel", roles: ["sales"], desc: "Mengelola pembatalan order marketplace." },
    { name: "Ventedaily — Shipping", roles: ["sales", "accounting"], desc: "Rekonsiliasi & status pengiriman order marketplace." },
    { name: "Ventedaily — Withdrawal", roles: ["accounting"], desc: "Pencairan dana marketplace (segera hadir)." },
    { name: "Ventedaily — Hermes API Docs", roles: [], desc: "Dokumentasi API internal (Hermes) untuk kebutuhan integrasi. Khusus Owner/teknis." },
    { name: "Ventedaily — Konektor ChatGPT", roles: [], desc: "Menghubungkan ERP ke ChatGPT (via MCP) agar bisa tanya-jawab data read-only. Hanya Owner." },
    { name: "Bazar", roles: ["sales"], desc: "Mengelola penjualan event/bazar: buat event, catat transaksi, dan bandingkan performa antar event." },
    { name: "Customer", roles: ["sales"], desc: "Data pelanggan & reseller." },
    { name: "Discount", roles: ["sales"], desc: "Aturan diskon yang bisa dipakai di kasir." },
  ]},
  { section: "Inventory", menus: [
    { name: "Product", roles: ["warehouse_admin"], desc: "Master produk & varian (warna, ukuran, SKU, harga jual, HPP)." },
    { name: "Product Label", roles: ["warehouse_admin"], desc: "Cetak label / barcode produk." },
    { name: "Mapping Varian Marketplace", roles: ["warehouse_admin", "sales"], desc: "Memetakan SKU marketplace ke varian internal agar order online cocok dengan stok." },
    { name: "Transfer Stock", roles: ["warehouse_admin", "supervisor"], desc: "Memindahkan stok antar gudang: draft → kirim → terima, dengan bukti transfer." },
    { name: "Stock In", roles: ["warehouse_admin"], desc: "Pencatatan barang masuk (segera hadir)." },
    { name: "Stock Out", roles: ["warehouse_admin"], desc: "Pengeluaran stok non-penjualan (penyesuaian keluar)." },
    { name: "Stock Prediction", roles: ["warehouse_admin"], desc: "Prediksi & usulan restock; menandai best seller yang rawan habis." },
    { name: "Business Intelligence", roles: ["accounting"], desc: "Analitik bisnis & penjualan lebih mendalam." },
    { name: "Stok Opname", roles: ["warehouse_admin", "supervisor"], desc: "Menghitung fisik stok vs sistem lalu melakukan penyesuaian." },
    { name: "Defect", roles: ["warehouse_admin", "qc_team"], desc: "Mencatat barang cacat agar keluar dari stok jual." },
    { name: "Category / Colour / Size", roles: ["warehouse_admin"], desc: "Master kategori, warna, dan ukuran produk." },
    { name: "Purchase Request", roles: ["purchasing"], desc: "Pengajuan permintaan pembelian sebelum dibuat PO." },
    { name: "Purchase Order", roles: ["purchasing"], desc: "Order pembelian resmi ke supplier." },
    { name: "Purchase PO", roles: ["purchasing"], desc: "Purchase Order khusus untuk kebutuhan pre-order." },
    { name: "Return Customer", roles: ["sales", "accounting"], desc: "Retur barang dari pelanggan. Otomatis memotong Net Sales & Net HPP di laporan." },
    { name: "Return Supplier", roles: ["purchasing"], desc: "Retur barang ke supplier (segera hadir)." },
    { name: "Supplier", roles: ["purchasing"], desc: "Data supplier." },
    { name: "Tailor", roles: ["production_admin", "purchasing"], desc: "Data penjahit/konveksi." },
    { name: "Warehouse — Product List", roles: ["warehouse_admin"], desc: "Daftar stok produk per gudang." },
    { name: "Warehouse — Stock Warehouse", roles: ["warehouse_admin"], desc: "Stok detail per gudang (passed / keep / alokasi penjualan)." },
    { name: "Warehouse — Pantau Stok Gudang", roles: ["warehouse_admin", "supervisor"], desc: "Rekomendasi transfer otomatis (Top 30 produk) dengan tombol autofill ke draft Transfer Gudang." },
    { name: "Warehouse — Stock Movement", roles: ["warehouse_admin", "accounting"], desc: "Riwayat seluruh mutasi stok." },
    { name: "Warehouse — Warehouse & Rack", roles: ["warehouse_admin"], desc: "Master gudang dan rak penyimpanan." },
  ]},
  { section: "Production", menus: [
    { name: "Overview", roles: ["production_admin", "production_spv"], desc: "Ringkasan status produksi." },
    { name: "Board Production", roles: ["production_admin", "production_spv", "cutting"], desc: "Papan kanban untuk memantau alur produksi antar tahap." },
    { name: "Material — Fabrics", roles: ["production_admin"], desc: "Stok kain (satuan yard/meter — jangan dijumlah lintas satuan)." },
    { name: "Material — Accessories", roles: ["production_admin"], desc: "Stok aksesoris produksi." },
    { name: "Material — Purchase Material", roles: ["production_admin", "purchasing"], desc: "Pembelian bahan baku produksi." },
    { name: "Material — Usulan Restock", roles: ["production_admin"], desc: "Usulan restock bahan berdasarkan kebutuhan." },
    { name: "Planning — Production Planning", roles: ["production_spv", "rnd"], desc: "Menyusun rencana produksi (wizard) dari kebutuhan." },
    { name: "Planning — BOM", roles: ["rnd"], desc: "Bill of Materials / resep produk (bahan & jumlah per produk)." },
    { name: "Planning — History Product", roles: ["production_admin", "production_spv"], desc: "Riwayat produk yang pernah diproduksi." },
    { name: "Production — Cutting", roles: ["cutting", "production_admin"], desc: "Order pemotongan kain." },
    { name: "Production — Garment", roles: ["production_admin"], desc: "Order jahit ke konveksi." },
    { name: "Production — Receive Goods", roles: ["production_admin", "production_spv", "qc_team"], desc: "Penerimaan hasil produksi dari konveksi." },
    { name: "Production — Outstanding WIP", roles: ["production_spv"], desc: "Barang yang masih dalam proses (Work In Progress)." },
    { name: "Production — Sisa Kain", roles: ["production_admin"], desc: "Pencatatan pengembalian sisa kain." },
    { name: "Production — Defect & Rework", roles: ["qc_team", "production_admin"], desc: "Catatan cacat produksi dan perbaikannya." },
  ]},
  { section: "Marketing", menus: [
    { name: "KOL Management", roles: [], desc: "Mengelola KOL/influencer: endorse, biaya, dan hasilnya. (Owner / tim marketing)" },
  ]},
  { section: "Accounting", menus: [
    { name: "Finance — Cost", roles: ["accounting"], desc: "Pencatatan biaya / HPP." },
    { name: "Finance — Expense", roles: ["accounting"], desc: "Pencatatan pengeluaran operasional." },
    { name: "Finance — Marketplace Settlement", roles: ["accounting"], desc: "Pencairan dan biaya dari marketplace." },
    { name: "Finance — Reconciliation Control Center", roles: ["accounting"], desc: "Pusat kendali seluruh proses rekonsiliasi." },
    { name: "Finance — Rekonsiliasi BCA", roles: ["accounting"], desc: "Mencocokkan mutasi rekening BCA dengan transaksi." },
    { name: "Finance — Crosscheck Pengiriman", roles: ["accounting"], desc: "Mengecek biaya/ongkir pengiriman vs tagihan." },
    { name: "Finance — Cakupan Laporan Online", roles: ["accounting"], desc: "Memastikan semua order online sudah tercatat di laporan." },
    { name: "Finance — Mapping Toko", roles: ["accounting"], desc: "Memetakan toko marketplace ke entitas internal." },
    { name: "RAB — Pengajuan Saya", roles: ["all"], desc: "Mengajukan RAB/anggaran (bisa oleh semua pengaju)." },
    { name: "RAB — Review Accounting", roles: ["accounting"], desc: "Review pengajuan RAB oleh accounting." },
    { name: "RAB — Persetujuan RAB", roles: [], desc: "Menyetujui RAB oleh pemilik/penanggung jawab." },
    { name: "RAB — Pendanaan", roles: ["accounting"], desc: "Mengalokasikan dana (partisi kas) untuk RAB disetujui." },
    { name: "RAB — Pembayaran", roles: ["accounting"], desc: "Membayar RAB yang sudah disetujui & didanai." },
    { name: "RAB — Saldo & Realisasi", roles: ["accounting"], desc: "Memantau saldo kas dan realisasi anggaran." },
    { name: "RAB — Konfigurasi PJ (Owner)", roles: [], desc: "Mengatur penanggung jawab & role khusus RAB. Hanya Owner." },
  ]},
  { section: "Report", menus: [
    { name: "Reports", roles: ["accounting", "supervisor"], desc: "Laporan penjualan, keuangan, dan stok; bisa diekspor ke Excel (termasuk kolom Qty & Nilai Return)." },
    { name: "Production Report", roles: ["production_spv"], desc: "Laporan hasil & progres produksi." },
  ]},
  { section: "User and Role", menus: [
    { name: "User & Roles", roles: [], desc: "Mengelola akun pengguna dan peran utamanya. Hanya Owner." },
    { name: "Custom Role", roles: [], desc: "Membuat peran kustom dengan izin granular per menu & per aksi. Hanya Owner." },
    { name: "User Activity", roles: [], desc: "Log aktivitas dan audit seluruh pengguna. Hanya Owner." },
  ]},
  { section: "System", menus: [
    { name: "Settings", roles: [], desc: "Pengaturan umum, branding (logo & nama aplikasi), dan otomasi. Hanya Owner." },
    { name: "Printer", roles: ["sales"], desc: "Pengaturan printer & format struk." },
    { name: "Customer Group", roles: ["sales"], desc: "Grup pelanggan (mis. harga khusus reseller)." },
    { name: "Payment Type", roles: [], desc: "Metode pembayaran yang tersedia di kasir. Hanya Owner." },
    { name: "POS Integration — Synchronize POS", roles: ["warehouse_admin"], desc: "Sinkronisasi data dari POS (Ventedaily)." },
    { name: "POS Integration — Import Purchase POS", roles: ["purchasing"], desc: "Mengimpor data pembelian dari POS." },
    { name: "POS Integration — History Import", roles: ["warehouse_admin"], desc: "Riwayat proses impor data." },
    { name: "POS Integration — Reconciliation POS", roles: ["accounting", "warehouse_admin"], desc: "Rekonsiliasi data POS dengan ERP." },
  ]},
];

// ================= Alur Kerja (bergambar) =================
const FLOWS = [
  { title: "Order Marketplace (Desty)", roles: ["sales", "picker", "packer"], steps: [
    { icon: RefreshCw, label: "Sinkron Desty" },
    { icon: PackageCheck, label: "Auto-Keep stok" },
    { icon: Receipt, label: "Kasir otomatis" },
    { icon: ScanLine, label: "Picking" },
    { icon: Package, label: "Packing" },
    { icon: Truck, label: "Serah ke kurir" },
  ]},
  { title: "Penjualan Kasir (POS)", roles: ["sales"], steps: [
    { icon: ShoppingCart, label: "Pilih produk" },
    { icon: Tag, label: "Diskon" },
    { icon: CreditCard, label: "Bayar" },
    { icon: Printer, label: "Cetak struk" },
    { icon: PackageMinus, label: "Stok berkurang" },
  ]},
  { title: "Keep / Titipan", roles: ["sales"], steps: [
    { icon: BookMarked, label: "Buat Keep + DP" },
    { icon: Wallet, label: "Pelunasan" },
    { icon: ArrowRightLeft, label: "Konversi ke penjualan" },
    { icon: Truck, label: "Kirim" },
  ]},
  { title: "Pre-Order", roles: ["sales", "production_admin", "production_spv"], steps: [
    { icon: CalendarClock, label: "Buka Batch & Kuota" },
    { icon: ClipboardList, label: "Create Keep PO" },
    { icon: BarChart3, label: "Analisis kebutuhan" },
    { icon: Factory, label: "Produksi" },
    { icon: PackageCheck, label: "Pesanan dipenuhi" },
    { icon: Truck, label: "Kirim" },
  ]},
  { title: "Produksi", roles: ["rnd", "production_admin", "production_spv", "cutting", "qc_team"], steps: [
    { icon: Layers, label: "Planning / BOM" },
    { icon: Scissors, label: "Cutting kain" },
    { icon: Shirt, label: "Jahit konveksi" },
    { icon: ClipboardCheck, label: "Receive + QC" },
    { icon: Package, label: "Stok jadi" },
  ]},
  { title: "Pembelian (Purchasing)", roles: ["purchasing", "warehouse_admin"], steps: [
    { icon: FileText, label: "Purchase Request" },
    { icon: ShoppingBag, label: "Purchase Order" },
    { icon: PackagePlus, label: "Terima barang" },
    { icon: Boxes, label: "Stok bertambah" },
  ]},
  { title: "RAB (Anggaran)", roles: ["accounting"], steps: [
    { icon: FilePlus, label: "Pengajuan" },
    { icon: Search, label: "Review Accounting" },
    { icon: CheckCircle, label: "Persetujuan" },
    { icon: Wallet, label: "Pendanaan" },
    { icon: CreditCard, label: "Pembayaran" },
    { icon: BarChart3, label: "Realisasi" },
  ]},
  { title: "Retur Pelanggan", roles: ["sales", "accounting"], steps: [
    { icon: Undo2, label: "Return Customer" },
    { icon: ClipboardList, label: "Retur tercatat" },
    { icon: TrendingDown, label: "Net Sales & HPP turun" },
  ]},
];

// ================= Tutorial Langkah-demi-Langkah =================
const TUTORIALS = [
  {
    title: "Cara Mengecek Paket yang Harus Dikirim Hari Ini",
    roles: ["sales", "packer", "picker"],
    audience: "Tim Sales dan admin pengiriman, serta Packer/Picker yang menyiapkan paket.",
    when: "Gunakan setiap pagi sebelum menyiapkan pengiriman dan sebelum kurir datang mengambil paket.",
    steps: [
      "Buka menu Ventedaily → Paket Belum Dikirim. Di sini terkumpul semua pesanan marketplace yang belum diserahkan ke kurir.",
      "Lihat tulisan \"Terakhir sinkron dari Desty\" di bagian bawah daftar. Deadline adalah batas waktu paket harus diserahkan ke kurir. Jika waktunya sudah lama, klik tombol \"Refresh dari Desty\" di kanan atas dan tunggu sampai muncul tulisan \"Data paket diperbarui\".",
      "Periksa empat kotak ringkasan di atas: Terlambat, Segera (<3 jam), Wajib hari ini, dan Total belum dikirim.",
      "Klik tab \"Perlu Tindakan\" untuk melihat paket paling mendesak (gabungan Terlambat, Segera, dan Wajib hari ini).",
      "Dahulukan paket di tab \"Terlambat\", lalu \"Segera\", lalu \"Hari Ini\". Mulai dari yang sisa waktunya paling sedikit.",
      "Gunakan kotak \"Cari No. Order / toko / pembeli\" jika ingin menemukan satu pesanan tertentu.",
      "Siapkan barang untuk tiap paket, lalu serahkan ke kurir.",
    ],
    reading: [
      { info: "Terlambat (angka merah)", mean: "paket yang batas waktunya sudah lewat.", todo: "siapkan dan serahkan ke kurir paling dulu." },
      { info: "Segera (<3 jam)", mean: "kurang dari 3 jam menuju batas waktu.", todo: "segera siapkan sebelum terlambat." },
      { info: "Wajib hari ini", mean: "harus diserahkan ke kurir hari ini.", todo: "masukkan ke antrean pengiriman hari ini." },
      { info: "Sisa Waktu", mean: "jam tersisa; tulisan \"lewat X jam\" berarti sudah telat sekian jam.", todo: "makin kecil atau minus, makin didahulukan." },
      { info: "Total belum dikirim", mean: "jumlah seluruh paket.", todo: "jika angkanya lebih banyak dari daftar yang terlihat, buka tab \"Semua\" karena daftar sedang disaring oleh tab/pencarian." },
    ],
    example: "Jika kotak Terlambat menunjukkan angka 1 dan di tab Terlambat ada pesanan dengan Sisa Waktu \"lewat 5 jam\", berarti paket itu sudah melewati batas waktu. Kamu perlu menyiapkannya lebih dulu dan segera menyerahkannya ke kurir.",
    done: "Pekerjaan selesai saat semua paket Terlambat dan Wajib hari ini sudah diserahkan ke kurir. Setelah kurir memindai/menerima paket, pesanan itu hilang sendiri dari daftar pada sinkron berikutnya. Menyiapkan paket = tindakan kamu; hilang dari daftar = status sudah tercatat di aplikasi.",
    issues: [
      { problem: "Angka paket 0 atau muncul tulisan data belum tersedia.", todo: "Klik \"Refresh dari Desty\", tunggu sampai muncul \"Data paket diperbarui\", lalu periksa lagi.", contact: "admin Ventedaily/Desty bila tetap kosong." },
      { problem: "Satu pesanan tidak muncul padahal menurut toko belum dikirim.", todo: "Pastikan sudah Refresh. Pesanan yang sudah dipindai kurir atau sudah dibatalkan memang tidak ditampilkan.", contact: "admin Ventedaily bila masih ragu." },
    ],
  },
  {
    title: "Cara Membuat Transaksi Penjualan di Kasir",
    roles: ["sales"],
    audience: "Tim Sales / kasir.",
    when: "Saat melayani pembeli yang membeli langsung, atau menyelesaikan pembayaran dari Keep.",
    steps: [
      "Buka menu Sale → Cashier.",
      "Jika penjualan berasal dari titipan, isi kolom \"Keep Code\". Keep adalah barang yang sebelumnya dititip/dibooking pembeli. Jika jualan langsung, lewati kolom ini.",
      "Pilih \"Customer\" (nama pembeli), dan pilih \"Group\" bila pembeli punya harga khusus (misalnya reseller).",
      "Tambahkan produk: cari produknya, pilih Color (warna) dan Size (ukuran), lalu isi jumlahnya.",
      "Atur \"Diskon\" bila ada, dan isi \"Ongkos Kirim\" bila barang dikirim.",
      "Pilih \"Payment Type\" (cara bayar) dan isi \"Cash Received\" (uang yang diterima). Kolom \"Change\" (kembalian) terhitung otomatis.",
      "Klik tombol simpan untuk menyelesaikan transaksi, lalu cetak struk.",
    ],
    reading: [
      { info: "Grand Total", mean: "total akhir yang harus dibayar pembeli.", todo: "cocokkan dengan uang yang diterima." },
      { info: "Change (Kembalian)", mean: "uang kembali untuk pembeli.", todo: "serahkan sesuai angka ini." },
      { info: "Diambil dari gudang (tertera di struk)", mean: "dari gudang mana stok diambil.", todo: "ambil barang dari gudang tersebut." },
    ],
    example: "Jika Grand Total Rp 150.000 dan Cash Received Rp 200.000, maka Change menunjukkan Rp 50.000. Kamu perlu mengembalikan Rp 50.000 ke pembeli.",
    done: "Transaksi selesai saat struk tercetak dan transaksinya muncul di menu Sale → Sale List. Stok produk otomatis berkurang.",
    issues: [
      { problem: "Produk atau varian tidak ketemu saat dicari.", todo: "Periksa ejaan, atau pastikan produk sudah terdaftar di menu Product.", contact: "Admin Gudang." },
      { problem: "Stok tidak mencukupi.", todo: "Cek ketersediaan di menu Warehouse → Stock Warehouse, atau lakukan transfer stok lebih dulu.", contact: "Admin Gudang / SPV." },
    ],
  },
  {
    title: "Cara Memindahkan Stok Antar Gudang",
    roles: ["warehouse_admin", "supervisor"],
    audience: "Admin Gudang dan SPV Gudang.",
    when: "Saat stok di satu gudang menipis tetapi gudang lain masih ada, atau saat memenuhi permintaan cabang.",
    steps: [
      "Buka menu Inventory → Product → Transfer Stock. Daftar transfer yang pernah dibuat muncul di sini.",
      "Klik tombol untuk membuat transfer baru. Halaman \"Form Transfer Stock\" akan terbuka.",
      "Pilih \"Transfer From\" (gudang asal) dan \"Transfer To\" (gudang tujuan).",
      "Tambahkan barang: cari \"Product\", pilih Color dan Size, lalu isi jumlah yang dipindah.",
      "Simpan transfer. Status awalnya draft, artinya transfer dibuat tetapi barang belum dikirim.",
      "Saat barang dikirim, ubah status menjadi dikirim; saat gudang tujuan menerima, tandai sudah diterima.",
    ],
    reading: [
      { info: "Status Draft", mean: "transfer baru dibuat, barang belum berangkat.", todo: "siapkan barang lalu tandai dikirim." },
      { info: "Status Dikirim / Diterima", mean: "progres perjalanan barang.", todo: "gudang tujuan memeriksa fisik lalu menandai diterima." },
    ],
    example: "Jika Gudang Bandung kehabisan ukuran M sedangkan Gudang Pusat masih 20 pcs, buat transfer dari Pusat ke Bandung sebanyak yang dibutuhkan.",
    done: "Transfer selesai saat gudang tujuan menandai barang \"diterima\". Setelah itu stok di gudang tujuan bertambah dan gudang asal berkurang.",
    issues: [
      { problem: "Jumlah yang bisa dipindah lebih kecil dari kebutuhan.", todo: "Cek sisa stok gudang asal di menu Warehouse → Stock Warehouse.", contact: "SPV Gudang." },
    ],
  },
  {
    title: "Cara Memakai Pantau Stok Gudang",
    roles: ["warehouse_admin", "supervisor"],
    audience: "Admin Gudang dan SPV Gudang.",
    when: "Saat ingin tahu barang apa yang sebaiknya dikirim ke satu gudang tanpa mengecek satu per satu.",
    steps: [
      "Buka menu Inventory → Warehouse → Pantau Stok Gudang.",
      "Pilih gudang tujuan yang ingin diisi.",
      "Lihat daftar rekomendasi (30 barang yang paling perlu dikirim) beserta kolom Prioritas.",
      "Klik tombol isi-otomatis untuk langsung menyiapkan Draft Transfer dari rekomendasi tersebut.",
      "Periksa dan sesuaikan jumlahnya, lalu simpan transfer.",
      "Bila perlu, cetak \"Picking List\" sebagai panduan pengambilan barang di gudang.",
    ],
    reading: [
      { info: "Prioritas", mean: "seberapa mendesak barang perlu dikirim.", todo: "dahulukan prioritas tertinggi." },
      { info: "Qty Siapkan (di Picking List)", mean: "jumlah yang harus diambil dari rak.", todo: "ambil barang sesuai angka tersebut." },
    ],
    example: "Jika sebuah varian ber-Prioritas tinggi dan direkomendasikan 12 pcs, siapkan 12 pcs untuk dikirim ke gudang tujuan.",
    done: "Selesai saat Draft Transfer tersimpan, lalu diproses seperti transfer biasa (ditandai dikirim lalu diterima).",
    issues: [
      { problem: "Daftar rekomendasi kosong.", todo: "Pastikan gudang tujuan sudah dipilih dan ada riwayat penjualan.", contact: "SPV Gudang." },
    ],
  },
  {
    title: "Cara Mengeluarkan Kain & Mencatat Hasil Cutting",
    roles: ["cutting", "production_admin"],
    audience: "Tim Cutting dan Admin Produksi.",
    when: "Saat rencana produksi sudah disetujui dan kain siap dipotong.",
    steps: [
      "Buka menu Production → Production → Cutting.",
      "Buat cutting baru: pada \"Pilih rencana (sudah disetujui)\" pilih nomor rencana produksi, lalu pilih \"Internal\" atau partner konveksi, isi target ukuran dan jumlah, lalu klik \"Simpan\".",
      "Buka detail cutting, lalu klik \"Keluarkan Kain\" untuk mencatat kain yang dipakai. Satuan kain umumnya yard.",
      "Setelah memotong, klik \"Catat Hasil & Sisa\" untuk mencatat jumlah hasil potongan dan sisa kain.",
      "Jika salah mengeluarkan kain, klik \"Balikkan Kain\" untuk mengembalikan kain ke stok, perbaiki rencana, lalu keluarkan ulang.",
    ],
    reading: [
      { info: "Status (Material Dikeluarkan / Sedang Cutting / Selesai)", mean: "tahap pekerjaan cutting saat ini.", todo: "lanjutkan menekan tombol sesuai tahapnya." },
      { info: "Lot / Roll Asal", mean: "gulungan kain mana yang dipakai.", todo: "pilih roll yang benar agar stok kain tetap akurat." },
    ],
    example: "Jika rencana meminta 50 pcs ukuran M dan kamu baru memotong 30 pcs, catat 30 pcs lebih dulu; statusnya menjadi \"Selesai Sebagian\".",
    done: "Selesai saat hasil potong dan sisa kain tercatat serta statusnya \"Selesai\". Stok kain berkurang sesuai jumlah yang dikeluarkan.",
    issues: [
      { problem: "Rencana tidak muncul saat membuat cutting.", todo: "Pastikan rencana sudah berstatus disetujui di menu Planning → Production Planning.", contact: "SPV Produksi." },
    ],
  },
  {
    title: "Cara Menerima Barang Jadi dari Konveksi",
    roles: ["production_admin", "production_spv", "qc_team"],
    audience: "Admin Produksi, SPV Produksi, dan Tim QC. QC adalah pemeriksaan kualitas barang.",
    when: "Saat hasil jahitan datang dari penjahit/konveksi dan perlu dimasukkan ke stok.",
    steps: [
      "Buka menu Production → Production → Receive Goods.",
      "Pilih order produksi yang barangnya datang.",
      "Isi jumlah yang diterima per ukuran. Kamu bisa klik \"Isi Semua Sesuai Sisa\" untuk mengisi cepat, atau \"Kosongkan\" untuk mengulang.",
      "Periksa kualitas barang (QC). Barang bagus masuk ke stok; barang cacat dicatat terpisah (lihat tutorial Mencatat Barang Cacat).",
      "Simpan penerimaan. Bila perlu, cetak \"Nota Harga Jahit\".",
    ],
    reading: [
      { info: "Sisa (yang belum diterima)", mean: "jumlah yang masih ditunggu dari konveksi.", todo: "terima sesuai fisik yang datang; sisanya menyusul di penerimaan berikutnya." },
    ],
    example: "Jika order 100 pcs dan yang datang 60 pcs, isi 60. Sisa 40 pcs tetap tercatat sebagai belum diterima.",
    done: "Selesai saat penerimaan tersimpan dan stok barang jadi bertambah di gudang. Memeriksa fisik = tindakan kamu; stok bertambah = status tercatat di aplikasi.",
    issues: [
      { problem: "Jumlah fisik lebih banyak dari sisa order.", todo: "Hitung ulang; jangan melebihi jumlah order tanpa persetujuan.", contact: "SPV Produksi." },
    ],
  },
  {
    title: "Cara Mencatat Barang Cacat (Defect)",
    roles: ["qc_team", "warehouse_admin"],
    audience: "Tim QC dan Admin Gudang.",
    when: "Saat menemukan barang cacat yang tidak layak dijual sebagai stok normal.",
    steps: [
      "Buka menu Inventory → Product → Defect.",
      "Klik \"Buat Defect\".",
      "Pilih \"Gudang Asal\", lalu cari produk yang cacat.",
      "Pilih \"Kategori Defect\" dan isi \"Keterangan Detail\" (contoh: noda kopi di bagian depan).",
      "Klik \"Simpan\". Barang akan dipindah ke Gudang Defect, terpisah dari stok normal.",
    ],
    reading: [
      { info: "Tab Daftar Defect / Tab Laporan", mean: "daftar semua barang cacat dan rekapnya.", todo: "pantau jumlah barang cacat secara berkala." },
    ],
    example: "Jika ada 2 pcs blouse dengan jahitan lepas, buat Defect 2 pcs dengan kategori yang sesuai agar tidak ikut terjual sebagai barang normal.",
    done: "Selesai saat defect tersimpan dan barang pindah ke Gudang Defect. Stok normal otomatis berkurang.",
    issues: [
      { problem: "Salah memasukkan data defect.", todo: "Buka detailnya, gunakan tombol \"Kembalikan ... pcs\" dengan mengisi alasan pengembalian.", contact: "SPV Gudang." },
    ],
  },
  {
    title: "Cara Mencatat Retur dari Pelanggan",
    roles: ["sales", "accounting"],
    audience: "Tim Sales dan Accounting.",
    when: "Saat pembeli mengembalikan barang, baik dari nota penjualan maupun pesanan marketplace.",
    steps: [
      "Buka menu Inventory → Return → Return Customer.",
      "Klik \"Create\".",
      "Pada kolom pencarian nota, ketik \"No. Sales\" (misalnya SALES0003) atau Order ID, lalu klik \"Cari\".",
      "Pilih barang yang diretur: isi \"Qty Return\" (jumlah dikembalikan), pilih \"Kondisi\" barang, dan pilih \"Gudang Tujuan\" (ke gudang mana barang kembali).",
      "Isi kolom \"Catatan\" untuk alasan retur bila perlu.",
      "Klik \"Proses Return\".",
    ],
    reading: [
      { info: "Sisa", mean: "jumlah yang masih bisa diretur dari nota itu.", todo: "jangan mengisi melebihi angka ini." },
      { info: "Nilai / Refund", mean: "nilai barang yang dikembalikan.", todo: "gunakan sebagai dasar pengembalian dana bila ada." },
    ],
    example: "Jika pembeli mengembalikan 1 dari 3 pcs di nota SALES0003, cari notanya, isi Qty Return 1, pilih Kondisi dan Gudang Tujuan, lalu klik Proses Return.",
    done: "Selesai saat retur tersimpan dan muncul di daftar Return Customer. Nilai penjualan bersih dan modal barang otomatis berkurang di laporan. HPP adalah harga pokok/modal barang.",
    issues: [
      { problem: "Nota tidak ditemukan.", todo: "Pastikan nomor nota atau Order ID benar; coba cari memakai Order ID.", contact: "Supervisor Sales." },
    ],
  },
  {
    title: "Cara Mengajukan & Menyetujui Anggaran (RAB)",
    roles: ["accounting"],
    audience: "Semua pengaju anggaran, Tim Accounting (memeriksa), dan Owner/penanggung jawab (menyetujui). RAB adalah Rencana Anggaran Biaya, yaitu pengajuan dana/kebutuhan divisi.",
    when: "Saat sebuah divisi membutuhkan dana untuk pembelian atau kegiatan dan perlu persetujuan.",
    steps: [
      "Buka menu Accounting → Finance → RAB & Pengajuan.",
      "Pada tab \"Pengajuan Saya\", klik \"Buat Pengajuan\". Jika tombol tidak aktif, akunmu belum terhubung ke sebuah divisi.",
      "Isi rincian kebutuhan dan jumlah dana, lalu kirim pengajuan.",
      "Tim Accounting membuka tab \"Review Accounting\" lalu klik \"Review\" untuk memeriksa pengajuan.",
      "Owner/penanggung jawab membuka tab \"Persetujuan RAB\" lalu klik \"Persetujuan RAB\" untuk menyetujui, atau \"Tolak\" bila ditolak.",
      "Setelah disetujui dan didanai, pembelian dicatat di tab \"Pembelian\" dengan tombol \"Catat Pembelian\".",
    ],
    reading: [
      { info: "Menunggu Review", mean: "pengajuan menunggu diperiksa Accounting.", todo: "tunggu, atau ingatkan Accounting." },
      { info: "Menunggu Approval", mean: "menunggu disetujui Owner/penanggung jawab.", todo: "tunggu keputusan persetujuan." },
      { info: "Sisa Pencairan", mean: "dana yang sudah disetujui tetapi belum dibelanjakan.", todo: "lanjutkan proses pembelian." },
      { info: "Bukti Belum Lengkap", mean: "pembelian perlu dilengkapi bukti.", todo: "unggah/lampirkan bukti yang kurang." },
    ],
    example: "Jika pengajuanmu berstatus \"Menunggu Review\", berarti Accounting belum memeriksanya. Kamu perlu menunggu atau mengingatkan Accounting.",
    done: "Pengajuan selesai saat statusnya disetujui dan (bila sudah dibeli) pembelian tercatat. Mengirim pengajuan = tindakan kamu; perubahan status = tercatat di aplikasi.",
    issues: [
      { problem: "Tombol \"Buat Pengajuan\" tidak bisa diklik.", todo: "Akunmu belum terhubung ke sebuah divisi.", contact: "Owner / admin yang mengatur divisi." },
      { problem: "Tab Review atau Persetujuan tidak muncul.", todo: "Tab itu hanya tampil bila kamu punya hak akses (Accounting/Owner/penanggung jawab).", contact: "Owner." },
    ],
  },
];

const matchRole = (roles, sel) =>
  sel === "all" || sel === "owner" || roles.includes("all") || roles.includes(sel);

const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

function buildPrintHtml(role, q) {
  const s = (q || "").toLowerCase();
  const rtag = (roles) => roles.includes("all") ? "Semua role" : roles.length ? roles.map((r) => ROLE_LABELS[r]).join(", ") : "Owner";
  const roleCards = ROLES
    .filter((r) => role === "all" || r.key === role || r.key === "owner")
    .filter((r) => !s || `${ROLE_LABELS[r.key]} ${r.desc}`.toLowerCase().includes(s));
  const flows = FLOWS.filter((f) => matchRole(f.roles, role));
  const roleTxt = role === "all" ? "Semua Peran" : ROLE_LABELS[role];

  const rolesHtml = roleCards.map((r) =>
    `<div class="item"><b>${esc(ROLE_LABELS[r.key])}</b><p>${esc(r.desc)}</p></div>`).join("");
  const flowsHtml = flows.map((f) =>
    `<div class="flow"><b>${esc(f.title)}</b><div class="steps">${f.steps.map((st) => esc(st.label)).join(" &rarr; ")}</div></div>`).join("");
  const tuts = TUTORIALS.filter((t) => matchRole(t.roles, role));
  const tutsHtml = tuts.map((t) => `
    <div class="tut">
      <h3>${esc(t.title)}</h3>
      <p><b>Untuk siapa:</b> ${esc(t.audience)}</p>
      <p><b>Kapan digunakan:</b> ${esc(t.when)}</p>
      <p><b>Langkah penggunaan:</b></p>
      <ol>${t.steps.map((s) => `<li>${esc(s)}</li>`).join("")}</ol>
      <p><b>Cara membaca informasi:</b></p>
      <ul>${t.reading.map((r) => `<li><b>${esc(r.info)}:</b> ${esc(r.mean)} <i>Yang perlu dilakukan:</i> ${esc(r.todo)}</li>`).join("")}</ul>
      <p class="egp"><b>Contoh ilustrasi:</b> ${esc(t.example)}</p>
      <p><b>Tanda selesai:</b> ${esc(t.done)}</p>
      <p><b>Kalau ada kendala:</b></p>
      <ul>${t.issues.map((is) => `<li><b>Masalah:</b> ${esc(is.problem)}<br><b>Yang dilakukan:</b> ${esc(is.todo)}<br><b>Hubungi:</b> ${esc(is.contact)}</li>`).join("")}</ul>
    </div>`).join("");
  const secHtml = SECTIONS.map((sec) => {
    const menus = sec.menus
      .filter((m) => matchRole(m.roles, role))
      .filter((m) => !s || `${m.name} ${m.desc}`.toLowerCase().includes(s));
    if (!menus.length) return "";
    return `<h3>${esc(sec.section)}</h3>` + menus.map((m) =>
      `<div class="item"><b>${esc(m.name)}</b> <span class="tag">${esc(rtag(m.roles))}</span><p>${esc(m.desc)}</p></div>`).join("");
  }).join("");

  return `<!doctype html><html lang="id"><head><meta charset="utf-8"><title>Panduan ERP Ventedaily</title>
<style>
  *{box-sizing:border-box} body{font-family:Arial,Helvetica,sans-serif;color:#3b2f2a;margin:32px;line-height:1.5}
  h1{font-size:22px;margin:0 0 2px} h2{font-size:16px;margin:24px 0 8px;border-bottom:2px solid #d8cfc6;padding-bottom:4px;color:#5a4a42}
  h3{font-size:13px;margin:14px 0 6px;color:#7a6a60} .sub{color:#8a7d74;font-size:12px;margin-bottom:16px}
  .item{margin:6px 0;padding:6px 10px;border-left:3px solid #e6ddd4;page-break-inside:avoid}
  .item b{font-size:12.5px} .item p{margin:2px 0 0;font-size:12px;color:#6b5d54}
  .tag{font-size:10px;background:#fdf1dc;color:#8a6616;padding:1px 6px;border-radius:8px}
  .flow{margin:6px 0;padding:6px 10px;background:#f7f2ec;border-radius:8px;page-break-inside:avoid}
  .flow b{font-size:12.5px} .steps{font-size:12px;color:#6b5d54;margin-top:2px}
  .tut{margin:10px 0;padding:8px 12px;border:1px solid #e6ddd4;border-radius:8px;page-break-inside:avoid}
  .tut h3{margin:0 0 4px;color:#5a4a42;font-size:13.5px} .tut p{margin:5px 0;font-size:12px}
  .tut ol,.tut ul{margin:3px 0 6px;padding-left:20px;font-size:12px;color:#5b4d44} .tut li{margin:2px 0}
  .tut .egp{background:#fbf5ea;border-left:3px solid #e3b96b;padding:5px 8px;border-radius:0 6px 6px 0}
  @media print{body{margin:14mm}}
</style></head><body>
  <h1>Panduan ERP Ventedaily</h1>
  <div class="sub">Peran: ${esc(roleTxt)}${q ? ` &middot; Pencarian: "${esc(q)}"` : ""} &middot; Dicetak ${new Date().toLocaleDateString("id-ID")}</div>
  <h2>Panduan Role (Peran)</h2>${rolesHtml}
  ${flows.length ? `<h2>Alur Kerja</h2>${flowsHtml}` : ""}
  ${tuts.length ? `<h2>Tutorial Langkah-demi-Langkah</h2>${tutsHtml}` : ""}
  <h2>Panduan Menu</h2>${secHtml}
</body></html>`;
}

const FlowDiagram = ({ flow }) => (
  <Panel className="p-4 mb-3" data-testid={`flow-${flow.title}`}>
    <div className="font-semibold text-sm text-cocoa mb-3">{flow.title}</div>
    <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-stretch gap-2">
      {flow.steps.map((s, i) => {
        const Icon = s.icon;
        return (
          <React.Fragment key={i}>
            <div className="flex sm:flex-col items-center gap-2 sm:gap-1.5 bg-ivory/60 border border-taupe/30 rounded-xl px-3 py-2 sm:w-28 sm:text-center">
              <div className="w-9 h-9 shrink-0 rounded-full bg-cocoa/10 flex items-center justify-center">
                <Icon className="w-4 h-4 text-cocoa" />
              </div>
              <span className="text-xs text-cocoa font-medium leading-tight">{s.label}</span>
            </div>
            {i < flow.steps.length - 1 && (
              <div className="flex items-center justify-center text-taupe self-center">
                <ChevronRight className="w-4 h-4 hidden sm:block" />
                <ChevronDown className="w-4 h-4 sm:hidden" />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  </Panel>
);

const Part = ({ label, children }) => (
  <div className="mt-3">
    <div className="text-[11px] font-bold uppercase tracking-wide text-amber-700 mb-1">{label}</div>
    {children}
  </div>
);

const TutorialCard = ({ t }) => {
  const [open, setOpen] = useState(false);
  return (
    <Panel className="mb-3 overflow-hidden" data-testid={`tutorial-${t.title}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`tutorial-toggle-${t.title}`}
        className="w-full flex items-start gap-2 px-4 py-3 text-left hover:bg-cocoa/5 transition-colors">
        {open ? <ChevronDown className="w-4 h-4 text-cocoa mt-0.5 shrink-0" /> : <ChevronRight className="w-4 h-4 text-cocoa mt-0.5 shrink-0" />}
        <span className="font-semibold text-sm text-cocoa">{t.title}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-taupe/20 text-sm text-wmuted">
          <Part label="Panduan ini untuk siapa?"><p className="leading-relaxed">{t.audience}</p></Part>
          <Part label="Kapan fitur ini digunakan?"><p className="leading-relaxed">{t.when}</p></Part>
          <Part label="Langkah penggunaan">
            <ol className="list-decimal pl-5 space-y-1.5 leading-relaxed">{t.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
          </Part>
          <Part label="Cara membaca informasi di halaman">
            <ul className="space-y-1.5">{t.reading.map((r, i) => (
              <li key={i} className="leading-relaxed"><b className="text-cocoa">{r.info}:</b> {r.mean} <span className="text-cocoa/80">Yang perlu dilakukan:</span> {r.todo}</li>
            ))}</ul>
          </Part>
          <Part label="Contoh ilustrasi">
            <p className="leading-relaxed bg-ivory/60 border-l-2 border-amber-300 pl-3 py-1.5 rounded-r">{t.example}</p>
          </Part>
          <Part label="Bagaimana tahu pekerjaan sudah selesai?"><p className="leading-relaxed">{t.done}</p></Part>
          <Part label="Kalau ada kendala">
            <ul className="space-y-2">{t.issues.map((is, i) => (
              <li key={i} className="leading-relaxed">
                <div><b className="text-cocoa">Masalah:</b> {is.problem}</div>
                <div><b className="text-cocoa">Yang dilakukan:</b> {is.todo}</div>
                <div><b className="text-cocoa">Hubungi:</b> {is.contact}</div>
              </li>
            ))}</ul>
          </Part>
        </div>
      )}
    </Panel>
  );
};

const SectionBlock = ({ sec, q, role }) => {
  const [open, setOpen] = useState(true);
  const menus = useMemo(() => {
    let list = sec.menus.filter((m) => matchRole(m.roles, role));
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((m) => `${m.name} ${m.desc}`.toLowerCase().includes(s));
    }
    return list;
  }, [sec.menus, q, role]);
  if (!menus.length) return null;
  return (
    <Panel className="mb-4 overflow-hidden" data-testid={`guide-section-${sec.section}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`guide-toggle-${sec.section}`}
        className="w-full flex items-center gap-2 px-4 py-3 bg-cocoa/5 hover:bg-cocoa/10 transition-colors">
        {open ? <ChevronDown className="w-4 h-4 text-cocoa" /> : <ChevronRight className="w-4 h-4 text-cocoa" />}
        <span className="font-heading font-bold text-cocoa">{sec.section}</span>
        <span className="ml-auto text-xs text-wmuted">{menus.length} menu</span>
      </button>
      {open && (
        <div className="divide-y divide-taupe/20">
          {menus.map((m) => (
            <div key={m.name} className="px-4 py-3" data-testid={`guide-menu-${m.name}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-sm text-cocoa">{m.name}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                  {m.roles.includes("all") ? "Semua role" : m.roles.length ? m.roles.map((r) => ROLE_LABELS[r]).join(", ") : "Owner"}
                </span>
              </div>
              <p className="text-sm text-wmuted mt-1 leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

export default function UserGuide() {
  const { user } = useAuth();
  const myRole = user?.role || "all";
  const [q, setQ] = useState("");
  const [role, setRole] = useState(myRole === "owner" ? "all" : myRole);

  const roleCards = useMemo(() => {
    let list = ROLES;
    if (role !== "all") list = list.filter((r) => r.key === role || r.key === "owner");
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((r) => `${ROLE_LABELS[r.key]} ${r.desc}`.toLowerCase().includes(s));
    }
    return list;
  }, [q, role]);

  const flows = useMemo(() => FLOWS.filter((f) => matchRole(f.roles, role)), [role]);
  const tutorials = useMemo(() => {
    let list = TUTORIALS.filter((t) => matchRole(t.roles, role));
    if (q) { const s = q.toLowerCase(); list = list.filter((t) => t.title.toLowerCase().includes(s)); }
    return list;
  }, [role, q]);

  const chips = [{ key: "all", label: "Semua peran" }, ...ROLES.map((r) => ({ key: r.key, label: ROLE_LABELS[r.key] }))];

  const downloadPdf = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(buildPrintHtml(role, q));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div data-testid="user-guide-page">
      <PageHeader title="Panduan ERP" subtitle="Penjelasan role, alur kerja, dan fungsi tiap menu — panduan untuk tim">
        <Button onClick={downloadPdf} className="gap-2" data-testid="guide-download-pdf">
          <Download className="w-4 h-4" /> Unduh PDF
        </Button>
      </PageHeader>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-wmuted" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari menu atau role..." className="bg-white pl-9" data-testid="guide-search" />
      </div>

      {/* Filter per Role */}
      <div className="mb-5">
        <div className="text-xs font-semibold text-cocoa mb-2">
          Panduan untuk peran: {role === "all" ? "Semua" : ROLE_LABELS[role]}
          {myRole !== "owner" && role === myRole && <span className="text-wmuted font-normal"> (peran Anda)</span>}
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="guide-role-filter">
          {chips.map((c) => (
            <button key={c.key} onClick={() => setRole(c.key)} data-testid={`role-chip-${c.key}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                role === c.key ? "bg-cocoa text-ivory border-cocoa" : "bg-white text-cocoa border-taupe/40 hover:bg-cocoa/5"
              }`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Panduan Role */}
      {roleCards.length > 0 && (
        <Panel className="mb-6 overflow-hidden" data-testid="guide-roles">
          <div className="flex items-center gap-2 px-4 py-3 bg-cocoa text-ivory">
            <ShieldCheck className="w-4 h-4" />
            <span className="font-heading font-bold">Panduan Role (Peran)</span>
          </div>
          <div className="grid gap-px bg-taupe/20 sm:grid-cols-2">
            {roleCards.map((r) => (
              <div key={r.key} className="bg-white px-4 py-3" data-testid={`guide-role-${r.key}`}>
                <div className="font-semibold text-sm text-cocoa">{ROLE_LABELS[r.key]}</div>
                <p className="text-sm text-wmuted mt-0.5 leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
          {!q && role === "all" && (
            <div className="px-4 py-3 border-t border-taupe/20 bg-ivory/40">
              <div className="text-xs font-semibold text-cocoa mb-1.5">Cara kerja izin akses:</div>
              <ul className="list-disc pl-5 space-y-1 text-sm text-wmuted">
                {ROLE_MODEL.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </div>
          )}
        </Panel>
      )}

      {/* Alur Kerja bergambar */}
      {flows.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <Workflow className="w-4 h-4 text-cocoa" />
            <h2 className="font-heading font-bold text-cocoa">Alur Kerja</h2>
          </div>
          {flows.map((f) => <FlowDiagram key={f.title} flow={f} />)}
        </>
      )}

      {/* Tutorial langkah-demi-langkah */}
      {tutorials.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-1 mt-6">
            <BookOpen className="w-4 h-4 text-cocoa" />
            <h2 className="font-heading font-bold text-cocoa">Tutorial Langkah-demi-Langkah</h2>
          </div>
          <p className="text-xs text-wmuted mb-3">Klik judul untuk membuka panduan lengkap tiap pekerjaan.</p>
          {tutorials.map((t) => <TutorialCard key={t.title} t={t} />)}
        </>
      )}

      {/* Panduan Menu */}
      <div className="flex items-center gap-2 mb-3 mt-6">
        <BookOpen className="w-4 h-4 text-cocoa" />
        <h2 className="font-heading font-bold text-cocoa">Panduan Menu</h2>
      </div>
      {SECTIONS.map((sec) => <SectionBlock key={sec.section} sec={sec} q={q} role={role} />)}
    </div>
  );
}
